import type {
  CreateCustomer,
  Customer,
  CreateCustomerBody
} from 'modules/Customers/types'
import { useNotifications } from '@sequencehq/core-components'
import omit from 'lodash/omit'
import { useAddTaxRegistration } from './useAddTaxRegistration'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api/utils'
import { isSequenceApiError, isSequenceApiErrorResponse } from '@sequencehq/api'
import { getAvalaraAddressError } from 'modules/Customers/utils/getAvalaraAddressError'
import { getCustomerAliasError } from '../utils/getCustomerAliasError'
import { getContactEmailError } from '../utils/getContactEmailError'
import { useNavigate } from 'react-router-dom'
import { toBillingOnlyAddresses } from '@sequencehq/api/utils/commonModels'

export type CreateCustomerErrorType =
  | 'INVALID_AVALARA_ADDRESS'
  | 'CUSTOMER_ALIAS_ALREADY_IN_USE'
  | 'CONTACT_EMAIL_ALREADY_EXISTS'
  | 'OTHER'

export type CreateCustomerError = {
  type: CreateCustomerErrorType
  identifiers: string[]
  messages: string[]
}

export type PerformCreateCustomer = ({
  body
}: {
  body: CreateCustomerBody
}) => Promise<Customer>

type Options = {
  // override the default create customer mutation
  performCreateCustomer?: PerformCreateCustomer
}

export const useCreateCustomer = (options?: Options) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const addCustomer = useMutation(dashboardv99990101Client.postCustomer)
  const { addTaxRegistration } = useAddTaxRegistration()

  return async (
    customer: CreateCustomer
  ): Promise<{
    customer: Customer | null
    error: CreateCustomerError | null
  }> => {
    const filterEmptyValues = (obj: object): object => {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([, value]) => value !== '' && value !== undefined)
          .map(([key, value]) =>
            value && typeof value === 'object' && !Array.isArray(value)
              ? [key, filterEmptyValues(value as object)]
              : [key, value]
          )
      )
    }

    const filteredCustomerBody = filterEmptyValues({
      ...customer,
      customerAliases: customer.customerAliases.filter(Boolean)
    }) as CreateCustomer
    /**
     * WARNING: some of these values, like customerAliases, will cause a
     * server error when saving them as empty arrays. It's therefore critical
     * to strip them out when empty!
     */
    const filteredAliases = customer.customerAliases?.filter(Boolean) ?? []
    const customerBody = {
      ...omit(filteredCustomerBody, [
        'taxRegistrations',
        'customerAliases',
        'address'
      ]),
      addresses: toBillingOnlyAddresses(filteredCustomerBody.address),
      ...(filteredAliases.length ? { customerAliases: filteredAliases } : {})
    }

    try {
      const performCreateCustomer =
        options?.performCreateCustomer ?? addCustomer.mutateAsync
      const saveResult = await performCreateCustomer({ body: customerBody })

      if (!saveResult) {
        return {
          customer: null,
          error: {
            type: 'OTHER',
            identifiers: [],
            messages: []
          }
        }
      }

      const registrationsToSave = (customer.taxRegistrations ?? []).filter(
        reg => reg.identifier.trim()
      )

      const taxRegistrationResults = await Promise.all(
        registrationsToSave.map(reg =>
          addTaxRegistration({
            taxIdentifier: reg.identifier,
            country: reg.country as Parameters<
              typeof addTaxRegistration
            >[0]['country'],
            state: ['US', 'CA'].includes(reg.country)
              ? (reg.state as Parameters<typeof addTaxRegistration>[0]['state'])
              : undefined,
            customerId: saveResult.id
          })
        )
      )

      if (taxRegistrationResults.some(result => !result)) {
        displayNotification('Failed to create tax registration for customer', {
          type: 'error'
        })
      }

      displayNotification('Customer created', {
        type: 'success',
        'data-testid': 'customerForm.createdToast',
        viewLabel: 'View customer',
        onClickView: () => navigate(`/customers/${saveResult.id}`)
      })

      return {
        customer: saveResult,
        error: null
      }
    } catch (e) {
      let errorType: CreateCustomerErrorType = 'OTHER'
      let identifiers: string[] = []

      const customerAliasError = getCustomerAliasError(e)

      if (customerAliasError.hasError) {
        errorType = 'CUSTOMER_ALIAS_ALREADY_IN_USE'
        identifiers = customerAliasError.identifiers
      }

      if (getContactEmailError(e).hasError) {
        errorType = 'CONTACT_EMAIL_ALREADY_EXISTS'
      }

      if (getAvalaraAddressError(e)) {
        errorType = 'INVALID_AVALARA_ADDRESS'
      }

      const messages: string[] = []
      if (isSequenceApiError(e) && isSequenceApiErrorResponse(e.response)) {
        messages.push(
          ...e.response.error.errors.map(err => err.message).filter(Boolean)
        )
      }

      return {
        customer: null,
        error: {
          type: errorType,
          identifiers,
          messages
        }
      }
    }
  }
}
